import {Job, Manifest, Skill, SkillOverrideNoteInput} from "../../generated/graphql";
import {TManifestStopEnhanced} from "../../components/common/types";
import {arrayContainsArray} from "../General";

export const extractIds = <T extends {id: number}>(items?: T[] | null) => items?.map((i) => i.id) ?? [];
type Selector =
  | ((row: Job | TManifestStopEnhanced, manifest: Manifest) => {jobIds: number[]; manifestIds: number[]})
  | ((
      row: Job | TManifestStopEnhanced,
      manifest: Manifest,
      activeSkills: Skill[]
    ) => {jobIds: number[]; manifestIds: number[]});
const doesManifestMeet = ({
  row,
  manifest,
  selector,
  activeSkills
}: {
  row: Job | TManifestStopEnhanced;
  manifest: Manifest;
  selector: Selector;
  activeSkills: Skill[];
}) => {
  const {jobIds, manifestIds} = selector(row, manifest, activeSkills);
  return arrayContainsArray(manifestIds, jobIds);
};
const requirementSelector = (job: Job | TManifestStopEnhanced, manifest: Manifest) => ({
  jobIds: extractIds(job.order?.requirements),
  manifestIds: extractIds(manifest.driver.qualifications)
});
const skillsSelector = (job: Job | TManifestStopEnhanced, manifest: Manifest, activeSkills: Skill[]) => {
  const activeSkillIds = activeSkills.map((skill) => skill.id);
  const jobSkillsRequirements = job.order?.orderSkills?.filter((skill) => activeSkillIds.includes(skill.id));
  return {
    jobIds: extractIds(jobSkillsRequirements),
    manifestIds: extractIds(manifest.driver.skills)
  };
};
export const getMatchRequirementManifests = ({
  selectedRows,
  manifests,
  isSkillsBasedRequirements,
  activeSkills
}: {
  selectedRows: Job[] | TManifestStopEnhanced[];
  manifests?: Manifest[];
  isSkillsBasedRequirements: boolean;
  activeSkills: Skill[];
}) => {
  if (!manifests || selectedRows.length === 0) return [];

  const selector = isSkillsBasedRequirements ? skillsSelector : requirementSelector;

  return manifests.filter((manifest) =>
    selectedRows.every((job) => doesManifestMeet({row: job, manifest, selector, activeSkills}))
  );
};

const MAX_NOTE_LENGTH = 255;
const MIN_REASON_LENGTH = 50;

/**
 * Truncate a skill name list so the prefix ("Missing: X, Y | ") stays within
 * a budget, leaving at least {@link MIN_REASON_LENGTH} chars for the reason.
 *
 * When all names don't fit, keeps as many as possible and appends "+N more".
 */
export const truncateSkillList = (skillNames: string[], maxPrefixLength: number): string => {
  const full = skillNames.join(", ");
  if (`Missing: ${full} | `.length <= maxPrefixLength) return full;

  let result = "";
  let included = 0;
  for (const name of skillNames) {
    const remaining = skillNames.length - included - 1;
    const suffix = remaining > 0 ? `, +${remaining} more` : "";
    const candidate = included === 0 ? name : `${result}, ${name}`;
    if (`Missing: ${candidate}${suffix} | `.length > maxPrefixLength && included > 0) {
      return `${result}, +${skillNames.length - included} more`;
    }
    result = candidate;
    included++;
  }
  return result;
};

/**
 * Build SkillOverrideNoteInput[] from per-job override reasons.
 *
 * For each job whose orderId has a reason, computes the skills the driver is
 * missing and formats the note as "Missing: SKILL1, SKILL2 | <reason text>".
 * When the skill list is too long, it truncates to "SKILL1, +N more" so the
 * total note fits within {@link MAX_NOTE_LENGTH}.
 */
export const buildSkillOverrideNotes = ({
  jobs,
  reasons,
  driverSkills,
  activeSkills
}: {
  jobs: Array<{jobId: number; order?: {orderId?: number | null; orderSkills?: Skill[] | null} | null}>;
  reasons: Record<number, string>;
  driverSkills: Skill[] | null | undefined;
  activeSkills: Skill[];
}): SkillOverrideNoteInput[] => {
  const activeSkillIds = new Set(activeSkills.map((s) => s.id));
  const driverSkillIds = new Set((driverSkills ?? []).map((s) => s.id));

  const notes: SkillOverrideNoteInput[] = [];
  const maxPrefixLength = MAX_NOTE_LENGTH - MIN_REASON_LENGTH;

  for (const job of jobs) {
    const reason = reasons[job.jobId];
    if (!reason?.trim() || !job.order?.orderId) continue;

    const requiredSkills = (job.order.orderSkills ?? []).filter((s) => activeSkillIds.has(s.id));
    const missingSkillNames = requiredSkills.filter((s) => !driverSkillIds.has(s.id)).map((s) => s.name);

    if (missingSkillNames.length === 0) continue;

    const skillList = truncateSkillList(missingSkillNames, maxPrefixLength);
    const note = `Missing: ${skillList} | ${reason.trim()}`;
    notes.push({orderId: job.order.orderId, note});
  }

  return notes;
};

/**
 * Build SkillOverrideNoteInput[] from per-job override reasons for stops
 * (used during reassignment).
 */
export const buildSkillOverrideNotesFromStops = ({
  stops,
  reasons,
  driverSkills,
  activeSkills
}: {
  stops: TManifestStopEnhanced[];
  reasons: Record<number, string>;
  driverSkills: Skill[] | null | undefined;
  activeSkills: Skill[];
}): SkillOverrideNoteInput[] => {
  // Deduplicate stops by jobId to avoid duplicate notes for the same order
  const uniqueJobs = Array.from(new Map(stops.map((s) => [s.job.jobId, s])).values());

  return buildSkillOverrideNotes({
    jobs: uniqueJobs.map((s) => ({
      jobId: s.job.jobId,
      order: s.order ? {orderId: s.order.orderId, orderSkills: s.order.orderSkills} : undefined
    })),
    reasons,
    driverSkills,
    activeSkills
  });
};
